import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Recipe, CONNECTOR_LABELS } from '@/types/database';
import { VoteButtons } from './VoteButtons';

interface RecipeCardProps {
  recipe: Recipe;
}

export function RecipeCard({ recipe }: RecipeCardProps) {
  const toolA = recipe.tool_a;
  const toolB = recipe.tool_b;

  return (
    <Link to={`/recipes/${recipe.slug}`}>
      <Card className="group h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden">
        <CardContent className="p-6">
          {/* Tool Icons */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center p-1.5 overflow-hidden">
                {toolA?.logo_url ? (
                  <img
                    src={toolA.logo_url}
                    alt={`${toolA.name} logo`}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-sm font-medium text-primary">
                    {toolA?.name?.charAt(0) || '?'}
                  </span>
                )}
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center p-1.5 overflow-hidden">
                {toolB?.logo_url ? (
                  <img
                    src={toolB.logo_url}
                    alt={`${toolB.name} logo`}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-sm font-medium text-primary">
                    {toolB?.name?.charAt(0) || '?'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Title & Subtitle */}
          <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {recipe.title}
          </h3>
          {recipe.subtitle && (
            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
              {recipe.subtitle}
            </p>
          )}

          {/* Meta & Voting */}
          <div className="flex items-center justify-between gap-2 mt-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {CONNECTOR_LABELS[recipe.connector]}
              </Badge>
              {recipe.confidence_score && recipe.confidence_score >= 80 && (
                <Badge variant="outline" className="text-xs gap-1 text-success border-success/30">
                  <CheckCircle2 className="h-3 w-3" />
                  Verified
                </Badge>
              )}
              {recipe.is_featured && (
                <Badge className="bg-gradient-primary text-white text-xs">
                  Featured
                </Badge>
              )}
            </div>
            <VoteButtons 
              recipeId={recipe.id} 
              upvoteCount={recipe.upvote_count || 0} 
              compact 
            />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
